<script setup>
import generateId from "@/composables/generateId";
import { computed, useSlots } from "vue";

const radioButtonId = generateId();

const props = defineProps({
  modelValue: {
    type: [Boolean, String, Number],
    default: false
  },
  label: {
    type: String,
    default: ""
  },
  message: {
    type: String,
    default: ""
  },
  disabled: {
    type: Boolean,
    default: false
  },
  error: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  },
  name: {
    type: String,
    default: ""
  },
  value: {
    type: [String, Number, Boolean],
    required: false,
    default: true
  },
  /** Подсветка label для состояния checked **/
  highlightActiveLabel: {
    type: Boolean,
    default: false
  },
});

const slots = useSlots();
const emit = defineEmits(["update:modelValue", "change"]);

const localCheckedValue = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    if (!props.readonly) {
      emit("update:modelValue", value);
    }
  }
});

const handleChange = () => {
  if (!props.readonly) {
    emit("change", localCheckedValue.value);
  }
};
</script>

<template>
  <label
    class="radio-button"
    :class="{
      'radio-button--disabled': disabled,
      'radio-button--error': error,
      'radio-button--readonly': readonly
    }"
    :for="radioButtonId"
  >
    <span class="radio-button__wrapper">
      <input
        :id="radioButtonId"
        v-model="localCheckedValue"
        :name="props.name || radioButtonId"
        :value="props.value"
        :class="{'radio-button__input--error': error}"
        class="radio-button__input"
        :disabled="disabled"
        :readonly="props.readonly"
        type="radio"
        :aria-disabled="disabled ? 'true' : 'false'"
        @change="handleChange"
      >
      <span
        v-if="props.label || slots['default']"
        class="radio-button__label"
        :class="{'radio-button__label--active': localCheckedValue === props.value && props.highlightActiveLabel}"
      >
        <template v-if="props.label">
          {{ props.label }}
        </template>
        <template v-if="slots['default']">
          <slot />
        </template>
      </span>
    </span>

    <span
      v-if="props.message"
      class="radio-button__message"
    >
      {{ message }}
    </span>
  </label>
</template>

<style scoped lang="scss">
.radio-button {
  display: flex;
  flex-direction: column;
  gap: 2px;
  cursor: pointer;

  &--readonly {
    pointer-events: none;
    cursor: default;
  }

  &__wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__label {
    color: var(--text-text-main);
    font-size: 14px;
    font-weight: 400;
    overflow-wrap: anywhere;
    cursor: pointer;

    &--active {
      color: var(--text-text-on);
    }
  }

  &__input {
    appearance: none;
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    width: 20px;
    height: 20px;
    border: 1px solid var(--icon-icon-secondary);
    border-radius: 50px;
    cursor: pointer;
    user-select: none;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &:disabled {
      pointer-events: none;
      border-color: var(--icon-icon-disabled);

      & + .radio-button__label {
        color: var(--text-04);
      }
    }

    &:checked {
      background-color: var(--white);
      border-color: var(--icon-icon-on);

      &::after {
        content: '';
        position: absolute;
        width: 9px;
        height: 9px;
        background-color: var(--icon-icon-on);
        border-color: var(--icon-icon-on);
        border-radius: 50%;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }

      &:not(:disabled):not(.radio-button__input--error) + .radio-button__label {
        color: var(--text-text-on);
      }

      &:disabled {
        border-color: var(--icon-icon-disabled);

        &::after {
          background-color: var(--icon-icon-disabled);
          border-color: var(--icon-icon-disabled);
        }
      }
    }

    &--error {
      border-color: var(--icon-icon-error);
    }

    &--error:checked {
      border-color: var(--icon-icon-error);

      &::after {
        background-color: var(--icon-icon-error);
        border-color: var(--icon-icon-error);
      }
    }
  }

  &__message {
    color: var(--text-text-error);
    font-size: 12px;
    font-weight: 400;
  }

  &:has(&__input:disabled) &__message {
    color: var(--text-04);
  }
}
</style>